import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Goal } from '@/types/experiment';
import { Target, Calendar, User, BarChart3, TrendingUp, Hash, ArrowLeft, Edit, Save, X } from 'lucide-react';

interface GoalViewProps {
  goal: Goal;
  onBack: () => void;
  onGoalUpdated: (updatedGoal: Goal) => void;
  propelauth: any;
}

export const GoalView: React.FC<GoalViewProps> = ({ goal, onBack, onGoalUpdated, propelauth }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editData, setEditData] = useState({
    name: goal.name,
    description: goal.description || '',
    success_metric: goal.success_metric,
    target_value: goal.target_value?.toString() || ''
  });
  const [error, setError] = useState<string | null>(null);

  // Get user ID from PropelAuth
  const getUserId = async () => {
    try {
      const authInfo = await propelauth.getAuthenticationInfoOrNull();
      return authInfo?.user?.userId || null;
    } catch (error) {
      console.error('Error getting user ID:', error);
      return null;
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      name: goal.name,
      description: goal.description || '',
      success_metric: goal.success_metric,
      target_value: goal.target_value?.toString() || ''
    });
    setError(null);
  };

  const handleSave = async () => {
    if (!editData.name.trim() || !editData.success_metric.trim()) {
      setError('Name and success metric are required');
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      const userId = await getUserId();
      if (!userId) {
        setError('Authentication required. Please refresh and try again.');
        return;
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'x-user-id': userId,
      };

      const payload = {
        external_id: goal.external_id,
        name: editData.name.trim(),
        description: editData.description.trim() || undefined,
        success_metric: editData.success_metric.trim(),
        target_value: editData.target_value ? parseFloat(editData.target_value) : undefined,
      };

      const response = await fetch('/api/goals', {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        const updatedGoal: Goal = {
          ...goal,
          ...data.goal,
          createdAt: goal.createdAt, // Keep original creation date
        };
        
        onGoalUpdated(updatedGoal);
        setIsEditing(false);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update goal');
      }
    } catch (error) {
      console.error('Error updating goal:', error);
      setError('Failed to update goal. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInputChange = (field: keyof typeof editData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEditData(prev => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null); // Clear error when user starts typing
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      case 'archived': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with back button and edit controls */}
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Goals
          </Button>
          <div className="flex items-center gap-2">
            <Target className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold">Goal Details</h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleCancel}
                disabled={isUpdating}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button 
                size="sm" 
                onClick={handleSave}
                disabled={isUpdating || !editData.name.trim() || !editData.success_metric.trim()}
              >
                <Save className="h-4 w-4 mr-1" />
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={handleEdit}>
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {/* Goal Header */}
      <div className="border-b pb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-4">
            {isEditing ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Goal Name *
                  </label>
                  <Input
                    value={editData.name}
                    onChange={handleInputChange('name')}
                    className="text-2xl font-bold h-auto py-2"
                    disabled={isUpdating}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Description
                  </label>
                  <Textarea
                    value={editData.description}
                    onChange={handleInputChange('description')}
                    className="text-lg resize-none"
                    rows={2}
                    disabled={isUpdating}
                  />
                </div>
              </>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-foreground">{goal.name}</h2>
                {goal.description && (
                  <p className="text-muted-foreground text-xl">{goal.description}</p>
                )}
              </>
            )}
          </div>
          <div className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(goal.status || 'active')}`}>
            {(goal.status || 'active').charAt(0).toUpperCase() + (goal.status || 'active').slice(1)}
          </div>
        </div>
      </div>

      {/* Goal Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <BarChart3 className="h-5 w-5 text-primary" />
            <span className="font-medium text-muted-foreground">Success Metric</span>
          </div>
          {isEditing ? (
            <Input
              value={editData.success_metric}
              onChange={handleInputChange('success_metric')}
              className="text-xl font-bold h-auto py-2"
              disabled={isUpdating}
            />
          ) : (
            <p className="text-2xl font-bold">{goal.success_metric}</p>
          )}
        </div>

        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="h-5 w-5 text-primary" />
            <span className="font-medium text-muted-foreground">Target Value</span>
          </div>
          {isEditing ? (
            <Input
              type="number"
              step="any"
              value={editData.target_value}
              onChange={handleInputChange('target_value')}
              placeholder="Optional"
              className="text-xl font-bold h-auto py-2"
              disabled={isUpdating}
            />
          ) : (
            <p className="text-2xl font-bold">{goal.target_value || 'Not set'}</p>
          )}
        </div>

        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="h-5 w-5 text-green-600" />
            <span className="font-medium text-muted-foreground">Current Best</span>
          </div>
          <p className="text-2xl font-bold">{goal.current_best || 0}</p>
        </div>

        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Hash className="h-5 w-5 text-primary" />
            <span className="font-medium text-muted-foreground">Experiments</span>
          </div>
          <p className="text-2xl font-bold">{goal.experiment_count || 0}</p>
        </div>
      </div>

      {/* Progress Section */}
      {goal.target_value && goal.current_best !== undefined && (
        <div className="bg-card border rounded-lg p-6">
          <h3 className="font-semibold text-xl mb-4">Progress to Target</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Current Progress</span>
              <span className="font-medium text-lg">
                {goal.current_best} / {goal.target_value}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-4">
              <div 
                className="bg-primary h-4 rounded-full transition-all duration-500"
                style={{ 
                  width: `${Math.min(100, (goal.current_best / goal.target_value) * 100)}%` 
                }}
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {((goal.current_best / goal.target_value) * 100).toFixed(1)}% complete
            </div>
          </div>
        </div>
      )}

      {/* Goal Metadata */}
      <div className="bg-card border rounded-lg p-6">
        <h3 className="font-semibold text-xl mb-4">Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground block text-sm">Created</span>
                <span className="font-medium">{formatDate(goal.createdAt)}</span>
              </div>
            </div>

            {goal.updated_at && (
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <span className="text-muted-foreground block text-sm">Last Updated</span>
                  <span className="font-medium">{formatDate(goal.updated_at)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {goal.external_id && (
              <div className="flex items-center gap-3">
                <Hash className="h-5 w-5 text-muted-foreground" />
                <div>
                  <span className="text-muted-foreground block text-sm">Goal ID</span>
                  <code className="font-mono text-sm bg-muted px-2 py-1 rounded">
                    {goal.external_id}
                  </code>
                </div>
              </div>
            )}

            {goal.organization_id && (
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-muted-foreground" />
                <div>
                  <span className="text-muted-foreground block text-sm">Organization</span>
                  <code className="font-mono text-sm bg-muted px-2 py-1 rounded">
                    {goal.organization_id}
                  </code>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Experiments Section */}
      <div className="bg-card border rounded-lg p-6">
        <h3 className="font-semibold text-xl mb-4">Related Experiments</h3>
        {goal.experiment_count === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Hash className="mx-auto h-12 w-12 mb-4 opacity-50" />
            <p>No experiments yet for this goal</p>
            <p className="text-sm mt-2">Create an experiment to start tracking progress</p>
          </div>
        ) : (
          <div className="text-muted-foreground">
            <p>{goal.experiment_count} experiment{goal.experiment_count !== 1 ? 's' : ''} associated with this goal</p>
            {/* TODO: Add experiments list here when experiments are implemented */}
          </div>
        )}
      </div>
    </div>
  );
}; 